import { ReactElement } from 'react';
import { PageSpinner } from 'components/atoms/PageSpinner';
import { useUser } from 'utils/auth';
import { useIsTeacherOrAdmin } from 'hooks/utils/useIsTeacherOrAdmin';
import { AccessDenied } from 'pages/in-app/AccessDenied';
import { PaywallPage } from 'pages/in-app/PaywallPage';
import { SubscriptionStatus, Role, SchoolUserRole } from '@novaheal/types';
import { isSubscriptionStatus } from 'utils/isSubscriptionStatus';
import { NH_MAILGUARD_ARTICLE_READ_LIMIT } from 'config/env';
import { MailWallPage } from 'pages/in-app/MailWallPage';
import { BetaWallPage } from 'pages/in-app/BetaWallPage';

type ProtectedRouteProps = {
  /**
   * Array of allowed school roles.
   * If provided, user must have at least one school role matching one of the values.
   *
   * @example
   * requireSchoolRoles={[SchoolUserRole.TEACHER, SchoolUserRole.ADMIN]}
   */
  requireSchoolRoles?: SchoolUserRole[];
  /**
   * If true, requires user to be a teacher OR admin.
   * Uses useIsTeacherOrAdmin hook which checks:
   * - educationStatus is 'TEACH' or 'GUIDE', OR
   * - school role is 'TEACHER', OR
   * - school role is 'ADMIN' OR 'TEACHER'
   *
   * @example
   * requireTeacherOrAdmin={true}
   */
  requireTeacherOrAdmin?: boolean;
  /**
   * If true, requires premium subscription (PAID, VOUCHER, INTERNAL, or TRIAL) to access this route.
   * If user doesn't meet requirement, PaywallPage will be rendered instead.
   *
   * @example
   * <ProtectedRoute requirePremiumAccess>
   *   <PremiumContent />
   * </ProtectedRoute>
   */
  requirePremiumAccess?: boolean;

  /**
   * If true, requires email verification to access this route.
   * If false, email verification is not required.
   */
  requireEmailVerified?: boolean;
  /**
   * If true, requires beta role (Role.BETA) to access this route.
   * If false, beta role is not required.
   */
  requireBeta?: boolean;
  /**
   * Child component to render.
   */
  children?: ReactElement;
};

/**
 * ProtectedRoute component for route-level authorization.
 *
 *
 * **All requirements use AND logic** - user must meet all specified requirements.
 *
 * Usage with teacher or admin requirement:
 * ```tsx
 * {
 *   path: `${ProtectedRoutes.WORKSHEET_EDITOR}/:id`,
 *   element: (
 *     <ProtectedRoute requireTeacherOrAdmin>
 *       <WorksheetEditor />
 *     </ProtectedRoute>
 *   ),
 * }
 * ```
 *
 * Usage with school roles requirement:
 * ```tsx
 * {
 *   path: ProtectedRoutes.MANAGEMENT,
 *   element: (
 *     <ProtectedRoute requireSchoolRoles={['TEACHER', 'ADMIN']}>
 *       <ManagementPage />
 *     </ProtectedRoute>
 *   ),
 * }
 * ```
 *
 * Usage with premium subscription requirement:
 * ```tsx
 * {
 *   path: ProtectedRoutes.PREMIUM_CONTENT,
 *   element: (
 *     <ProtectedRoute requirePremiumAccess>
 *       <PremiumContent />
 *     </ProtectedRoute>
 *   ),
 * }
 * ```
 *
 * Usage with email verification requirement:
 * ```tsx
 * {
 *   path: ProtectedRoutes.ARTICLE,
 *   element: (
 *     <ProtectedRoute requireEmailVerified>
 *       <Article />
 *     </ProtectedRoute>
 *   ),
 * }
 * ```
 * Note: Shows MailWallPage if user has reached the article read limit without email verification.
 *
 * Usage with beta role requirement:
 * ```tsx
 * {
 *   path: ProtectedRoutes.SESSION_PLAN,
 *   element: (
 *     <ProtectedRoute requireTeacherOrAdmin requireBeta>
 *       <SessionPlanListPage />
 *     </ProtectedRoute>
 *   ),
 * }
 * ```
 *
 * Usage with combined requirements (AND logic):
 * ```tsx
 * {
 *   path: ProtectedRoutes.PREMIUM_TEACHER,
 *   element: (
 *     <ProtectedRoute
 *       requireTeacherOrAdmin
 *       requirePremiumAccess
 *       requireEmailVerified
 *     >
 *       <PremiumTeacherPage />
 *     </ProtectedRoute>
 *   ),
 * }
 * ```
 */
export const ProtectedRoute = ({
  // requireEducationStatus,
  requireSchoolRoles,
  requireTeacherOrAdmin = false,
  requirePremiumAccess = false,
  requireEmailVerified = false,
  requireBeta = false,
  children,
}: ProtectedRouteProps): ReactElement | null => {
  const { data: user, isFetched } = useUser();
  const isTeacherOrAdmin = useIsTeacherOrAdmin();

  // Show loading spinner while user data is being fetched
  if (!isFetched || !user) {
    return <PageSpinner />;
  }

  // Check teacher or admin requirement
  if (requireTeacherOrAdmin && !isTeacherOrAdmin) {
    return <AccessDenied />;
  }

  // Check school roles requirement
  if (requireSchoolRoles && requireSchoolRoles.length > 0) {
    const rolePool: SchoolUserRole[] = (user?.schoolRole?.roles || []) as SchoolUserRole[];

    const hasRequiredSchoolRole = requireSchoolRoles.some((requiredRole: SchoolUserRole) =>
      rolePool.includes(requiredRole)
    );

    if (!hasRequiredSchoolRole) {
      return <AccessDenied />;
    }
  }

  // Check subscription requirement
  if (requirePremiumAccess) {
    const userSubscriptionStatus = user.subscription?.status;

    // If user has no subscription, show paywall
    if (!userSubscriptionStatus) {
      return <PaywallPage />;
    }

    // Check if user meets the required subscription level
    const hasRequiredSubscription =
      isSubscriptionStatus(user, SubscriptionStatus.PAID) ||
      isSubscriptionStatus(user, SubscriptionStatus.VOUCHER) ||
      isSubscriptionStatus(user, SubscriptionStatus.INTERNAL) ||
      isSubscriptionStatus(user, SubscriptionStatus.TRIAL);

    if (!hasRequiredSubscription) {
      // Show paywall page if subscription is required
      return <PaywallPage />;
    }
  }

  // Check email verification requirement
  if (requireEmailVerified) {
    if (!user.emailVerified) {
      const userLastViewedEntryLength = user?.lastViewedEntryIds.length ?? 0;
      const isArticleLimitReached = userLastViewedEntryLength >= NH_MAILGUARD_ARTICLE_READ_LIMIT;

      if (isArticleLimitReached) {
        return <MailWallPage />;
      }
    }
  }

  // Check beta requirement
  if (requireBeta) {
    if (user.role !== Role.BETA) {
      return <BetaWallPage />;
    }
  }

  // User has required access
  // Render children if provided
  return children || null;
};
